import { JSONSchema7, JSONSchema7Definition } from 'json-schema';
import { cloneDeep, pick } from 'lodash';
import { extractMixins, mixinToNormalForm } from '../../../mixins';
import { logicContainerDefinition, logicRuleDefinition, topLevelLogicRules } from './schemaFields';
import { ratingSliderTheme } from './schemaThemes';

export const questionSkipIfOnlySystemAnswers = {
  type: 'object',
  properties: {
    /* Пропустить вопрос если остались только системные ответы */
    skipIfOnlySystemAnswers: { type: 'boolean' },
  },
  required: [],
} as const;

export const imageGallerySchema = {
  type: 'object',
  properties: {
    previewHeight: { type: 'number' },
    previewWidth: { type: 'number' },
    previewSrc: { type: 'string' },
    preview: { type: 'string' },
    aspect: { type: 'number' },
    height: { type: 'number' },
    width: { type: 'number' },
    src: { type: 'string' },
    originalSrc: { type: 'string' },
  },
  additionalProperties: false,
  required: ['src'],
} as const;

export const questionVideo = {
  type: 'object',
  properties: {
    videoSrc: { type: 'string' },
    videoLabel: { type: 'string' },
    videoLabelPosition: { enum: ['BEFORE_MEDIAFILE', 'AFTER_MEDIAFILE'] },
    videoAutostart: {
      type: 'boolean',
      description: 'Запускать видео сразу при загрузке без действий со стороны пользователя',
    },
    videoAutorepeat: { type: 'boolean', description: 'Запускать видео в бесконечном цикле' },
    percentToUnlockMedia: { type: 'number' },
  },
  required: ['videoSrc'],
} as const;

export const questionAudio = {
  type: 'object',
  properties: {
    audioSrc: { type: 'string' },
    audioLabel: { type: 'string' },
    audioLabelPosition: { enum: ['BEFORE_MEDIAFILE', 'AFTER_MEDIAFILE'] },
    percentToUnlockMedia: { type: 'number' },
  },
  required: ['audioSrc'],
} as const;

export const imageSchema = {
  type: 'object',
  properties: {
    preview: { type: 'string' },
    height: { type: 'number' },
    width: { type: 'number' },
    src: { type: 'string' },
    originalSrc: { type: 'string' },
  },
  required: ['src'],
} as const;

export const questionHasHints = {
  type: 'object',
  properties: {
    successHint: { type: 'string' },
    errorHint: { type: 'string' },
  },
} as const;

export const questionHasMaximumRank = {
  type: 'object',
  properties: {
    maximumRank: { type: 'number' },
  },
} as const;

export const questionHasAnswerCountLimit = {
  type: 'object',
  properties: {
    hasAnswerCountLimit: { type: 'boolean' },
  },
  if: {
    properties: { hasAnswerCountLimit: { const: true } },
    required: ['hasAnswerCountLimit'],
  },
  then: {
    properties: {
      answerCountMinimum: { type: 'number' },
      answerCountMaximum: { type: 'number' },
    },
  },
  required: [],
} as const;

export const questionHideNotSelectedAnswers = {
  type: 'object',
  properties: {
    hideNotSelectedAnswers: { type: 'boolean' },
  },
} as const;

export const questionHasCorrectAnswer = {
  type: 'object',
  properties: {
    hasCorrectAnswer: { type: 'boolean' },
  },
} as const;

export const questionAllowRetryUntilCorrectAnswer = {
  type: 'object',
  if: {
    properties: { hasCorrectAnswer: { const: true } },
    required: ['hasCorrectAnswer'],
  },
  then: {
    properties: {
      allowRetryUntilCorrectAnswer: { type: 'boolean' },
    },
  },
} as const;

export const conditionHasCorrectAnswer = {
  type: 'object',
  properties: {},
  if: {
    properties: { hasCorrectAnswer: { const: true } },
    required: ['hasCorrectAnswer'],
  },
  then: {
    properties: {
      hasOthers: { const: false },
    },
  },
} as const;

export const questionHasAward = {
  type: 'object',
  properties: {
    hasAward: { type: 'boolean' },
  },
} as const;

export const questionAwardAmount = {
  type: 'object',
  properties: {
    hasAward: { type: 'boolean' },
  },
  if: {
    properties: { hasAward: { const: true } },
    required: ['hasAward'],
  },
  then: {
    properties: { awardAmount: { type: 'number', description: 'Вознаграждение, целое число' } },
    required: ['awardAmount'],
  },
} as const;

export const questionHasAwardMultiple = {
  type: 'object',
  oneOf: [
    {
      properties: {
        hasAward: { const: true },
        hasCorrectAnswer: { const: true },
        awardAmount: { type: 'number', description: 'Вознаграждение, целое число' },
      },
      required: ['hasAward', 'hasCorrectAnswer', 'awardAmount'],
    },
    {
      properties: { hasAward: { const: true }, hasCorrectAnswer: { const: false } },
      required: ['hasAward', 'hasCorrectAnswer'],
    },
    {
      properties: { hasAward: { const: false }, hasCorrectAnswer: { type: 'boolean' } },
      required: [],
    },
  ],
} as const;

export const questionAutoCommit = {
  type: 'object',
  properties: {
    autoCommit: { type: 'boolean' },
  },
} as const;

export const questionAutoNext = {
  type: 'object',
  description: 'Опция при вводе ответа автоматически переходить на следующий экран(Пропуск обратной связи)',
  properties: {
    autoNext: {
      type: 'boolean',
    },
  },
} as const satisfies JSONSchema7;

export const questionMultipleAppearance = {
  type: 'object',
  description: 'Вид каждого варианта в множественном выборе',
  properties: {
    multipleAnswerAppearance: {
      enum: ['checkbox', 'bubble'],
    },
  },
  required: [],
} as const satisfies JSONSchema7;

export const questionConjointLevel = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    name: { type: 'string' },
  },
  required: ['id', 'name'],
} as const;

export const questionConjointAttribute = {
  type: 'object',
  properties: {
    id: { type: 'string' },
    name: { type: 'string' },
    levels: {
      type: 'array',
      items: questionConjointLevel,
      minItems: 1,
    },
  },
  required: ['id', 'name', 'levels'],
} as const;
export const questionConjoint = {
  type: 'object',
  properties: {
    attributes: {
      type: 'array',
      items: questionConjointAttribute,
      minItems: 1,
    },
    exclusiveLevels: {
      type: 'object',
      description:
        'Объект, где ключ - уровень, значение - уровни, которые не могут быть использованы вместе с уровнем-ключом в карточке',
      patternProperties: {
        '.*': {
          type: 'array',
          items: { type: 'string' },
        },
      },
    },
    premadePlanSet: {
      type: 'object',
      description: 'Доступные планы показов - заранее сгенерированные наборы карточек для Conjoint',
      properties: {
        amount: {
          type: 'number',
          description: 'Количество доступных планов показов',
        },
        id: {
          type: 'string',
          description: 'Идентификатор плана показа',
        },
        url: {
          type: 'string',
          description: 'URL где находятся файлы плана показа',
        },
        hash: {
          type: 'string',
          description: 'Хеш Record<AttributeId, LevelId[]>, где ключи и массивы отсортированы в алфавитном порядке',
        },
      },
      required: ['amount', 'id', 'hash'],
    },
  },
  required: ['attributes', 'exclusiveLevels'],
} as const;

export const questionChoiceConjoint = {
  type: 'object',
  properties: {
    cardsOnScreen: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string' },
        },
        required: ['name'],
      },
      minItems: 1,
    },
    numberOfScreens: { type: 'number', minimum: 1 },
  },
  required: ['cardsOnScreen', 'numberOfScreens'],
} as const;

export const questionMaxDiff = {
  type: 'object',
  properties: {
    answersOnScreen: { type: 'number' },
    numberOfScreens: { type: 'number' },
    answersLabel: { type: 'string' },
    minimumLabel: { type: 'string' },
    maximumLabel: { type: 'string' },
  },
  required: ['answersOnScreen', 'numberOfScreens'],
} as const;

export const questionHasOthers = {
  type: 'object',
  properties: {
    hasOthers: { type: 'boolean' },
  },
  if: {
    properties: { hasOthers: { const: true } },
    required: ['hasOthers'],
  },
  then: {
    properties: {
      othersLimit: { type: 'number' },
      othersLabel: { type: 'string' },
      othersRequired: { type: 'boolean' },
      othersPlaceholder: { type: 'string' },
      othersReplaceUnsafe: { type: 'boolean' },
    },
    required: ['othersRequired', 'othersReplaceUnsafe'],
  },
} as const;

export const questionRankHasOthers = {
  type: 'object',
  properties: {
    hasOthers: { type: 'boolean' },
  },
  if: {
    properties: { hasOthers: { const: true } },
    required: ['hasOthers'],
  },
  then: {
    properties: {
      othersLimit: { type: 'number' },
      othersPlaceholder: { type: 'string' },
      othersReplaceUnsafe: { type: 'boolean' },
    },
    required: ['othersReplaceUnsafe'],
  },
} as const;

export const questionHasTimer = {
  type: 'object',
  properties: {
    hasTimer: { type: 'boolean' },
  },
  if: {
    properties: { hasTimer: { const: true } },
    required: ['hasTimer'],
  },
  then: {
    properties: {
      timerStrict: { type: 'boolean' },
      timerDuration: { type: 'number' },
      timerExpiredText: { type: 'string' },
    },
    required: ['timerStrict', 'timerDuration'],
  },
} as const;

export const questionHasComment = {
  type: 'object',
  properties: {
    hasComment: { type: 'boolean' },
  },
  if: {
    properties: { hasComment: { const: true } },
  },
  then: {
    properties: {
      commentLimit: { type: 'number' },
      commentLabel: { type: 'string' },
      commentReplaceUnsafe: { type: 'boolean' },
    },
    required: ['commentLimit', 'commentReplaceUnsafe'],
  },
  required: ['hasComment'],
} as const;

export const questionHasGratitude = {
  type: 'object',
  properties: {
    hasGratitude: { type: 'boolean' },
  },
  if: {
    properties: { hasGratitude: { const: true } },
    required: ['hasGratitude'],
  },
  then: {
    properties: { gratitudeText: { type: 'string' } },
    required: ['gratitudeText'],
  },
} as const;

export const questionImagesAspectRatio = {
  type: 'object',
  properties: {
    imagesAspectRatio: { type: 'string' },
  },
  required: ['imagesAspectRatio'],
} as const;

export const questionImageBackground = {
  type: 'object',
  properties: {
    imageBackground: { type: 'string' },
  },
} as const;

export const questionImageMap = mixinToNormalForm({
  imageMap: {
    type: 'object',
    patternProperties: {
      '^.*$': {
        type: 'object',
        properties: {
          image: imageSchema,
        },
        required: ['image'],
        additionalProperties: false,
      },
    },
    additionalProperties: false,
  },
} as const);

export const questionImage = {
  type: 'object',
  properties: {
    image: imageSchema,
  },
} as const;

export const questionAuth = {
  type: 'object',
  properties: {
    authProvider: { enum: ['twork'] },
    skipAuthenticated: { type: 'boolean' },
  },
  required: ['authProvider'],
} as const;

export const questionSuccessNumber = mixinToNormalForm({
  successNumber: { type: 'number' },
} as const);

export const questionSuccessAnswer = mixinToNormalForm({
  successAnswer: { type: 'string' },
} as const);

export const questionNoneOptionText = mixinToNormalForm({
  noneOptionText: { type: 'string' },
} as const);

export const questionFixedRandomizeAnswers = {
  type: 'object',
  properties: {
    fixedRandomizeAnswers: {
      type: 'object',
      properties: {
        questionId: { type: 'string' },
      },
      required: [],
    },
  },
} as const;

export const questionRandomizeAnswers = {
  type: 'object',
  properties: {
    randomizeAnswers: { type: 'boolean' },
    randomizeAnswersExcludedIds: {
      items: { type: 'string' },
      type: 'array',
    },
  },
} as const;

export const questionRequiresAnswer = {
  type: 'object',
  properties: {
    requiresAnswer: { type: 'boolean' },
  },
} as const;

export const questionDeckId = {
  type: 'object',
  properties: {
    deckId: { type: 'string' },
  },
  required: [],
} as const;

export const questionText = {
  type: 'object',
  properties: {
    textareaType: {
      enum: ['TEXT', 'PHONE', 'EMAIL', 'DATE', 'NUMBER', 'URL', 'SINGLE_LINE_TEXT', 'SINGLE_LINES_TEXT'],
    },
    textLabel: { type: 'string' },
    textLimit: { type: 'number' },
    minimumLines: { type: 'number' },
    maximumLines: { type: 'integer', minimum: 1 },
    textReplaceUnsafe: { type: 'boolean' },
  },
  required: ['textareaType', 'textLimit', 'textReplaceUnsafe'],
} as const;

export const questionRatingSliderTheme = {
  type: 'object',
  properties: {
    theme: ratingSliderTheme,
  },
} as const;

export const questionRatingSliderLabels = {
  type: 'object',
  properties: {
    labels: {
      type: 'array',
      items: { type: 'string' },
    },
  },
  required: ['labels'],
} as const;

export const questionRatingSliderPreset = {
  type: 'object',
  properties: {
    preset: { enum: ['rating', 'ipsative'] },
  },
  required: ['preset'],
} as const;

export const questionRatingDigitsPreset = {
  type: 'object',
  properties: {
    preset: { enum: ['rating', 'csi', 'csat', 'nps'] },
  },
  required: ['preset'],
} as const;

export const questionRatingStarsPreset = {
  type: 'object',
  properties: {
    preset: { enum: ['rating', 'csi', 'csat'] },
  },
  required: ['preset'],
} as const;

export const matrixRatingPreset = {
  type: 'object',
  properties: {
    preset: { enum: ['rating', 'csi', 'csat'] },
  },
  required: ['preset'],
} as const;

export const questionRatingMaximum = {
  type: 'object',
  properties: {
    maximumValue: { type: 'number' },
  },
  required: ['maximumValue'],
} as const;

export const questionRatingMinimum = {
  type: 'object',
  properties: {
    minimumValue: { type: 'number' },
  },
  required: ['minimumValue'],
} as const;

export const questionRatingHasHints = {
  type: 'object',
  properties: {
    hasHints: { type: 'boolean' },
  },
  if: {
    properties: { hasHints: { const: true } },
  },
  then: {
    properties: {
      lowerHint: { type: 'string' },
      higherHint: { type: 'string' },
    },
  },
  required: ['hasHints'],
} as const;

export const questionFileType = {
  type: 'object',
  properties: {
    fileType: { enum: ['IMAGE', 'DOC', 'VIDEO', 'AUDIO', 'ANY'] },
    fileSizeLimit: { type: 'number' },
  },
  required: ['fileType'],
} as const;

export const questionRatingJustify = {
  type: 'object',
  properties: {
    justifyContent: { enum: ['flex-start', 'center', 'flex-end'] },
  },
} as const;

export const questionRatingSize = {
  type: 'object',
  properties: {
    ratingSize: { enum: ['large', 'medium', 'small'] },
  },
} as const;

export const questionHasNotApplicable = {
  type: 'object',
  properties: {
    hasNotApplicable: { type: 'boolean' },
  },
  if: {
    properties: { hasNotApplicable: { const: true } },
    required: ['hasNotApplicable'],
  },
  then: {
    properties: {
      notApplicableLabel: { type: 'string' },
    },
  },
} as const;

export const questionHasAnswersLimit = {
  type: 'object',
  properties: {
    answersLimit: { type: ['number', 'null'] },
  },
} as const;

export const questionInstantStatistic = {
  type: 'object',
  properties: {
    instantStatisticRequired: { type: 'boolean' },
  },
} as const;

export const questionExclusive = {
  type: 'object',
  properties: {
    exclusiveQuestion: { type: 'boolean' },
  },
  required: ['exclusiveQuestion'],
} as const;

export const questionCardSortingAllowUncomplete = {
  type: 'object',
  properties: {
    cardSortingAllowUncomplete: { type: 'boolean' },
  },
  required: ['cardSortingAllowUncomplete'],
} as const;

export const questionCardSortingType = {
  type: 'object',
  properties: {
    cardSortingType: { enum: ['closed'] },
  },
  required: ['cardSortingType'],
} as const;

export const questionCategoryForUnsortedCards = {
  type: 'object',
  properties: {
    categoryForUnsortedCards: { type: 'boolean' },
  },
  required: ['categoryForUnsortedCards'],
} as const;

export const systemButtonText = {
  type: 'object',
  properties: {
    systemButtonText: {
      type: 'object',
      properties: {
        next: { type: 'string' },
        answer: { type: 'string' },
        skip: { type: 'string' },
      },
    },
  },
} as const;

export const TEXT_OPERATIONS = ['TEXT_EQUALS', 'TEXT_NOT_EQUALS', 'TEXT_CONTAINS', 'TEXT_NOT_CONTAINS'] as const;
export const ANSWER_OPERATIONS = [
  'EQUALS',
  'NOT_EQUALS',
  'EQUALS_ANY_OF',
  'NOT_EQUALS_ANY_OF',
  'ANY_OF',
  'ALL_OF',
  'NOT_ANY_OF',
  'NOT_ALL_OF',
] as const;
export const NUMBER_OPERATIONS = [
  'MATH_EQUALS',
  'MATH_NOT_EQUALS',
  'GREATER_OR_EQUALS',
  'GREATER',
  'LESS_OR_EQUALS',
  'LESS',
  'ANSWER_COUNT_EQUALS',
  'ANSWER_COUNT_GREATER',
  'ANSWER_COUNT_LESS',
] as const;

export const ANSWERS_LOGIC_OPERATIONS = [...TEXT_OPERATIONS, ...ANSWER_OPERATIONS, ...NUMBER_OPERATIONS];

export const questionGoToRules = {
  goToRules: {
    type: 'array',
    items: { $ref: '#/definitions/goToRule' },
  },
} as const;

export const questionGoToRuleDefinition = {
  goToRule: {
    type: 'object',
    allOf: [
      {
        type: 'object',
        properties: {
          logicOperationType: {
            enum: ['OR'],
          },
          logicRules: {
            type: 'array',
            items: { $ref: '#/definitions/logicContainer' },
          },
        },
        required: ['logicRules', 'logicOperationType'],
      },
      {
        oneOf: [
          {
            properties: { goToType: { enum: ['question'] }, questionId: { type: 'string' } },
            required: ['questionId', 'goToType'],
          },
          {
            properties: { goToType: { enum: ['group'] }, groupId: { type: 'string' } },
            required: ['groupId', 'goToType'],
          },
          { properties: { goToType: { enum: ['final'] } }, required: ['goToType'] },
          {
            properties: { goToType: { enum: ['set'] }, setId: { type: 'string' } },
            required: ['setId', 'goToType'],
          },
        ],
      },
    ],
    unevaluatedProperties: false,
  },
} as const;

export const questionAppearanceAnswerImage = {
  type: 'object',
  description: 'Количество картинок, которое отображается в одном ряду для разных устройств',
  properties: {
    imagesPerRow: {
      type: 'object',
      properties: {
        desktop: {
          type: 'string',
          enum: ['one', 'two'],
        },
        mobile: {
          type: 'string',
          enum: ['one', 'two'],
        },
      },
      required: ['desktop', 'mobile'],
    },
  },
} as const;

export function createQuestionSchema<T extends Record<string, unknown>[]>(...schemas: T) {
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      groupId: { type: 'string' },
      setId: { type: 'string' },
      ...anchorDefinition,
      data: mergeObjectSchemas(
        {
          type: 'object',
          properties: {
            text: { type: 'string' },
            ...questionGoToRules,
            ...topLevelLogicRules,
          },
          required: ['text', 'type', 'logicRules'],
          unevaluatedProperties: false,
        } as const,
        ...schemas,
      ) as JSONSchema7Definition,
    },
    additionalProperties: false,
    required: ['id', 'data'],
    definitions: {
      ...logicRuleDefinition,
      ...logicContainerDefinition,
      ...questionGoToRuleDefinition,
      ...extractMixins(schemas),
    },
  } as const satisfies JSONSchema7;
}

export const anchorDefinition = {
  anchor: {
    description:
      'Якорь по которому можно определять сущность во внешних системах. Может содержать только буквы (латиница и кириллица), цифры и нижние подчёркивания и не может быть пустым',
    type: 'string',
    pattern: '^[a-zA-Zа-яА-Я_0-9]+$',
  } satisfies JSONSchema7Definition,
} as const;
export function createQuestionSchemaWithMixins<T extends JSONSchema7, Q extends string, A extends JSONSchema7>({
  mixins = {},
  answer,
  base,
  type,
}: {
  mixins: Record<string, JSONSchema7>;
  base?: T;
  type: Q;
  answer?: A;
}) {
  const localMixins = {
    questionGoToRules: { type: 'object', properties: questionGoToRules, required: [] },
    questionLogicRules: {
      type: 'object',
      properties: topLevelLogicRules,
      required: ['logicRules'],
    },
    questionBase: {
      type: 'object',
      properties: {
        text: { type: 'string' },
        description: { type: 'string' },
        systemButtonText: {
          type: 'object',
          properties: {
            next: { type: 'string' },
            answer: { type: 'string' },
            skip: { type: 'string' },
          },
        },
      },
      required: ['text'],
    },
  } satisfies Record<string, JSONSchema7>;
  const mergedMixins = { ...mixins, ...localMixins };
  const restDefinitions = {
    ...logicRuleDefinition,
    ...logicContainerDefinition,
    ...questionGoToRuleDefinition,
    ...(extractMixins(Object.values(mixins)) as Record<string, never>),
    ...mergedMixins,
    ...(answer ? (extractMixins([answer]) as Record<string, never>) : {}),
  } as const;
  return {
    type: 'object',
    properties: {
      id: { type: 'string' },
      groupId: { type: 'string' },
      deckId: { type: 'string' },
      setId: {
        type: 'string',
        description:
          'Идентификатор сета, к которому принадлежит вопрос. Если у вопроса есть groupId, то setId нужно брать из группы',
      },
      ...anchorDefinition,
      data: {
        type: 'object',
        unevaluatedProperties: false,
        allOf: [
          mergeObjectSchemas(
            {
              properties: { type: { enum: [type] } },
              required: ['type'],
            } as const,
            ...(answer
              ? [
                  {
                    properties: { answers: { type: 'array', items: { $ref: `#/definitions/${type}_ANSWER` } } },
                    required: ['answers'],
                  } as const,
                ]
              : []),
            base ?? {},
          ),
          ...Object.keys(mergedMixins).map(v => ({ $ref: `#/definitions/${v}` })),
        ],
      },
    },
    additionalProperties: false,
    required: ['id', 'data'],
    definitions: {
      ...(answer
        ? ({
            [`${type}_ANSWER`]: answer,
          } satisfies Record<`${string}_ANSWER`, typeof answer>)
        : {}),
      ...restDefinitions,
    } as typeof restDefinitions & Record<`${string}_ANSWER`, typeof answer | undefined>,
  } as const;
}

export type PickForce<O, P> = O extends O ? (P extends keyof O ? O[P] : never) : never;
export type PickForceArr<O, P> = O extends O
  ? P extends keyof O
    ? O[P] extends unknown[]
      ? O[P][number]
      : never
    : never
  : never;
export type PickForceProperties<O, P = 'properties'> = O extends O ? (P extends keyof O ? O[P] : O) : never;

export type AllKeys<T> = T extends T ? keyof T : never;

type MergeSchemas<
  B extends {
    properties?: Record<string, JSONSchema7>;
    allOf?: readonly unknown[];
    required?: readonly string[];
  } & Record<string, unknown>,
  S extends ({
    properties?: object;
    required?: readonly string[];
    if?: unknown;
    then?: unknown;
    else?: unknown;
  } & Record<string, unknown>)[],
> = Omit<B, 'properties' | 'required' | 'allOf'> & {
  properties: {
    [P in AllKeys<PickForceProperties<S[number]> | B['properties']>]: PickForce<
      PickForceProperties<S[number]> | B['properties'],
      P
    >;
  };
  required: [PickForceArr<S[number] | B, 'required'>];
  allOf: [PickContaining<S[number], 'if' | 'then' | 'else' | 'oneOf' | 'allOf'> | PickForceArr<B, 'allOf'>];
};

// todo: typetests instead of commented examples...

type PickContaining<O, K> = O extends O ? (Extract<keyof O, K> extends never ? never : PickWeak<O, K>) : never;

type PickWeak<O, K> = { [P in Extract<keyof O, K>]: O[P] };

export function mergeObjectSchemas<
  B extends {
    properties?: Record<string, JSONSchema7>;
    allOf?: readonly unknown[];
    required?: readonly string[];
  } & Record<string, unknown>,
  S extends ({
    properties?: Record<string, JSONSchema7>;
    required?: readonly string[];
    oneOf?: unknown;
    allOf?: unknown;
    if?: unknown;
    then?: unknown;
    else?: unknown;
  } & Record<string, unknown>)[],
>(baseSchema: B, ...schemas: S): MergeSchemas<B, S> {
  const resultSchema = cloneDeep(baseSchema) as unknown as {
    properties: Record<string, unknown>;
    allOf?: unknown[];
    required?: string[];
  };
  const conditionKeys = ['if', 'then', 'else', 'oneOf', 'allOf'] as const;

  resultSchema.required ??= [];

  for (const schema of schemas) {
    if (schema.properties == null && schema.oneOf == null && schema.if == null) {
      Object.assign(resultSchema.properties, schema);
      continue;
    }

    const conditions = pick(schema, conditionKeys);
    if (Object.keys(conditions).length > 0) {
      if (resultSchema.allOf == null) resultSchema.allOf = [];
      resultSchema.allOf.push(conditions);
    }

    Object.assign(resultSchema.properties, schema.properties);

    if (schema.required) {
      for (const required of schema.required) {
        if (!resultSchema.required?.includes(required)) resultSchema.required?.push(required);
      }
    }
  }

  const { properties, required, allOf, ...rest } = resultSchema;

  if (allOf) {
    return {
      ...rest,
      allOf: [
        {
          properties,
          required,
          type: 'object' as const,
        },
        ...allOf,
      ],
    } as any;
  }
  return resultSchema as any;
}
